"use client";

import { motion } from "framer-motion";
import { ListCheck } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useAppState } from "@/app/hooks/useAppState";
import useDeveloperOptions from "@/app/hooks/useDeveloperOptions";
import { useIngredients } from "@/app/hooks/useIngredients";
import { useListManager } from "@/app/hooks/useListManager";
import { useSync } from "@/app/hooks/useSync";
import { useVoiceRecognition } from "@/app/hooks/useVoiceRecognition";
import { useAuthGate } from "@/app/providers/AuthGateProvider";
import { ArchiveModal } from "@/components/ingredients/ArchiveModal";
import { CategorySection } from "@/components/ingredients/CategorySection";
import { IngredientActionBar } from "@/components/ingredients/IngredientActionBar";
import { IngredientInput } from "@/components/ingredients/IngredientInput";
import { ListModal } from "@/components/lists/ListModal";
import { ViewSharedListModal } from "@/components/lists/ViewSharedListModal";
import { StripeRedirectHandler } from "@/components/premium/StripeRedirectHandler";
import { EmptyState } from "@/components/shared/EmptyState";
import { ListDropdown } from "@/components/shared/ListDropdown";
import { LoadingState } from "@/components/shared/LoadingState";
import { PageHeader } from "@/components/shared/PageHeader";
import { VoiceTipModal } from "@/components/voice/VoiceTipModal";
import { trackEngagement } from "@/lib/analytics";
import { orderCategories } from "@/lib/categories";
import { APP_ENV } from "@/lib/env";
import { LIST_COLORS } from "@/lib/lists/colors";
import { hasVoiceTipBeenSeen, markVoiceTipAsSeen } from "@/lib/storage";
import type { Ingredient } from "@/lib/types";
import { PRIMARY_COLOR } from "@/lib/utils";

/**
 * Delay in milliseconds before resetting state after IngredientInput processes it.
 * This ensures the useEffect in IngredientInput has time to read and apply the values
 * before they're cleared. Keeping this small to avoid UI delays.
 */
const INPUT_PROCESSING_DELAY_MS = 100;

export default function Home() {
  const t = useTranslations("ingredients");
  const [showArchive, setShowArchive] = useState(false);
  const [showVoiceTip, setShowVoiceTip] = useState(false);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) {
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.documentElement.style.overflow = "";
      };
    }
  }, [isMounted]);

  useEffect(() => {
    console.log("App started:", {
      env: APP_ENV,
    });
  }, []);

  // Stable callback for notifications using toast
  const handleNotification = useCallback(
    (notif: { type: "success" | "error"; message: string }) => {
      if (notif.type === "success") {
        toast.success(notif.message);
      } else {
        toast.error(notif.message);
      }
    },
    []
  );

  // Sync hook - provides manual sync capability
  const { manualSync } = useSync();

  // App state hook - manages local state and operation queue (calls useSync internally)
  // IMPORTANT: We get syncState and isSyncing from useAppState because it manages
  // automatic sync operations (debounced every 2 seconds). The page's useSync instance
  // only handles manual syncs. Using useAppState's sync state ensures SyncStatus
  // animates during automatic syncs and shows badges after completion.
  const {
    state,
    setState,
    activeList,
    queueOperation,
    replaceOrQueueOperation,
    syncState,
    isSyncing,
  } = useAppState();

  const { addList, selectList, updateList, deleteList } = useListManager({
    state,
    setState,
    queueOperation,
  });

  // List management UI state
  const [editingList, setEditingList] = useState<any | null>(null);
  const [editListName, setEditListName] = useState("");
  const [editListColor, setEditListColor] = useState("");
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [newListName, setNewListName] = useState(t("myList"));
  const [newListColor, setNewListColor] = useState(PRIMARY_COLOR);
  const { devOptions } = useDeveloperOptions();
  const { requireAuth } = useAuthGate();
  const locale = useLocale();

  // Voice recognition state
  const [voiceTranscript, setVoiceTranscript] = useState<string>("");
  // Imported ingredients state
  const [importedIngredients, setImportedIngredients] = useState<string[]>([]);

  const {
    isListening,
    isProcessing,
    isSupported,
    isPermissionGranted,
    startListening,
    stopListening,
  } = useVoiceRecognition({
    locale: locale as "en" | "zh-CN" | "zh-TW",
    onResult: (transcript) => {
      setVoiceTranscript(transcript);
    },
    onError: (error) => {
      if (error === "not-allowed") {
        toast.error(t("voicePermissionDenied"));
      } else {
        toast.error(t("voiceError"));
      }
    },
  });

  // Voice toggle handler
  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      // Check if user has seen the voice tip
      const voiceTipSeen = hasVoiceTipBeenSeen();
      if (!voiceTipSeen && isSupported) {
        // Show tip modal first
        setShowVoiceTip(true);
      } else {
        // Proceed with starting listening (which will request permission)
        startListening();
      }
    }
  };

  // Reset transcript after it's been used
  useEffect(() => {
    if (voiceTranscript) {
      const timer = setTimeout(() => setVoiceTranscript(""), INPUT_PROCESSING_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [voiceTranscript]);

  // Reset imported ingredients after they've been used
  useEffect(() => {
    if (importedIngredients.length > 0) {
      const timer = setTimeout(() => setImportedIngredients([]), INPUT_PROCESSING_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [importedIngredients]);

  // Handle imported ingredients from URL
  const handleImportIngredients = (ingredients: string[]) => {
    setImportedIngredients(ingredients);
  };

  const handleEditList = (list: any) => {
    setEditingList(list);
    setEditListName(list.name);
    setEditListColor(list.color);
  };

  const handleUnshareList = async () => {
    if (!editingList?.isShared) return;

    try {
      // Call the leave endpoint
      const response = await fetch(`/api/lists/${editingList.id}/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      if (!response.ok) {
        // Show error toast with translated message
        toast.error(data.error || "Failed to leave list");
        return;
      }

      // Show success toast
      toast.success("You have left the shared list");

      // Close modal and reload to refresh the list
      setEditingList(null);
      window.location.reload(); // Simple reload to refresh shared lists
    } catch (error) {
      console.error("Failed to leave list:", error);
      toast.error("Failed to leave list");
    }
  };

  const handleSaveListEdit = () => {
    if (editingList && !editingList.isShared) {
      updateList(editingList.id, editListName, editListColor);
      setEditingList(null);
    }
  };

  const handleDeleteList = () => {
    if (editingList && !editingList.isShared) {
      deleteList(editingList.id);
      setEditingList(null);
    }
  };

  const handleAddList = () => {
    addList(newListName, newListColor);
    setShowAddListModal(false);
    setNewListName(t("myList"));
    setNewListColor(PRIMARY_COLOR);
  };

  const handleOpenAddModal = () => {
    setNewListName(t("myList"));
    setNewListColor(PRIMARY_COLOR);
    setShowAddListModal(true);
  };

  const {
    addIngredients,
    toggleIngredient,
    deleteIngredient,
    updateIngredient,
    archiveAllIngredients,
    deleteAllIngredients,
    incrementQuantity,
    decrementQuantity,
  } = useIngredients({
    activeList,
    setState,
    queueOperation,
    replaceOrQueueOperation,
  });

  // Developer-only functions
  const handleDevAddBulkIngredients = () => {
    const sampleIngredients = [
      // Meat (5)
      "chicken breast",
      "ground beef",
      "pork chops",
      "turkey sausage",
      "lamb stew meat",
      // Seafood (5)
      "salmon fillets",
      "canned tuna",
      "shrimp",
      "cod fillets",
      "tilapia",
      // Produce (10)
      "lettuce",
      "tomatoes",
      "onions",
      "garlic",
      "carrots",
      "broccoli",
      "spinach",
      "cucumbers",
      "bell peppers",
      "potatoes",
      // Fruit (6)
      "apples",
      "bananas",
      "oranges",
      "strawberries",
      "blueberries",
      "lemons",
      // Dairy (4)
      "milk",
      "cheddar cheese",
      "yogurt",
      "eggs",
    ];

    addIngredients(sampleIngredients);
  };

  // Categorize and sort ingredients, memoized to avoid recomputing on unrelated renders
  // Grouped by category, any category included (an agent may use its own), each group sorted by name
  const categorizedIngredients = useMemo(() => {
    const unsorted: Record<string, Ingredient[]> =
      activeList?.ingredients
        .filter((ing) => !ing.completed)
        .reduce(
          (acc, ing) => {
            if (!acc[ing.category]) {
              acc[ing.category] = [];
            }
            acc[ing.category].push(ing);
            return acc;
          },
          {} as Record<string, Ingredient[]>
        ) || ({} as Record<string, Ingredient[]>);

    return Object.keys(unsorted).reduce(
      (acc, category) => {
        acc[category] = [...unsorted[category]].sort((a, b) =>
          a.text.toLowerCase().localeCompare(b.text.toLowerCase())
        );
        return acc;
      },
      {} as Record<string, Ingredient[]>
    );
  }, [activeList?.ingredients]);

  const archivedIngredients = useMemo(
    () => activeList?.ingredients.filter((ing) => ing.completed) || [],
    [activeList?.ingredients]
  );

  // iOS-style stacking animation variants - subtle rubberband effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.04,
      },
    },
  };

  return (
    <>
      <PageHeader
        leftSlot={
          <ListDropdown
            lists={state.lists}
            activeList={activeList}
            onSelectList={selectList}
            onEditList={handleEditList}
            onAddList={handleOpenAddModal}
          />
        }
      />

      <main className="min-h-[calc(100dvh-var(--navbar-height))] overflow-x-clip">
        {!isMounted ? (
          <div className="fixed inset-0">
            <LoadingState />
          </div>
        ) : (
          <>
            {activeList && (
              <motion.div
                className="pt-4 pb-0 sm:pt-6 sm:pb-2"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                <div className="max-w-lg mx-auto px-3 space-y-3">
                  <IngredientInput
                    onAdd={addIngredients}
                    isListening={isListening}
                    isProcessing={isProcessing}
                    voiceTranscript={voiceTranscript}
                    importedIngredients={importedIngredients}
                  />
                  <IngredientActionBar
                    syncState={syncState}
                    isSyncing={isSyncing}
                    onManualSync={() => {
                      manualSync();
                      // Decided not to track manual syncs for now, this gets called a lot and kinda useless
                      // trackEngagement("sync_manual");
                    }}
                    onOpenArchive={() => {
                      setShowArchive(true);
                      trackEngagement("archive_opened");
                    }}
                    onArchiveAll={() => {
                      archiveAllIngredients();
                      trackEngagement("archive_all");
                    }}
                    onDeleteAll={() => {
                      deleteAllIngredients();
                      trackEngagement("delete_all");
                    }}
                    // Developer-only
                    isDeveloperMode={devOptions.developerMode}
                    onAddBulkIngredients={handleDevAddBulkIngredients}
                    hasActiveIngredients={
                      activeList
                        ? activeList!.ingredients.filter((ing) => !ing.completed).length > 0
                        : false
                    }
                    // Voice input
                    isListening={isListening}
                    isProcessing={isProcessing}
                    isSupported={isSupported}
                    isPermissionGranted={isPermissionGranted}
                    onVoiceToggle={handleVoiceToggle}
                    onImportIngredients={handleImportIngredients}
                    onRequireAuth={requireAuth}
                  />
                </div>
              </motion.div>
            )}

            <motion.div
              className="max-w-lg mx-auto px-3 py-4"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              {orderCategories(Object.keys(categorizedIngredients)).map((category) => {
                const ingredients = categorizedIngredients[category] || [];
                if (ingredients.length === 0) return null;

                return (
                  <CategorySection
                    key={category}
                    category={category}
                    ingredients={ingredients}
                    onToggleIngredient={toggleIngredient}
                    onDeleteIngredient={deleteIngredient}
                    onUpdateIngredient={updateIngredient}
                    onIncrementQuantity={incrementQuantity}
                    onDecrementQuantity={decrementQuantity}
                  />
                );
              })}

              {/* Show "no list" state if no non-disabled lists exist */}
              {!activeList && state.lists.filter((list) => !list.isDisabled).length === 0 && (
                <EmptyState
                  icon={
                    <div className="w-20 h-20 flex items-center justify-center">
                      <ListCheck className="size-full text-text-secondary" strokeWidth={1} />
                    </div>
                  }
                  title={t("emptyStateNoList")}
                  description={t("emptyStateNoListDescription")}
                />
              )}
              {/* Show "empty list" state if active list has no active ingredients */}
              {activeList &&
                activeList!.ingredients.filter((ing) => !ing.completed).length === 0 && (
                  <EmptyState
                    icon={
                      <div className="w-20 h-20 flex items-center justify-center">
                        <LoadingState className="size-32 sm:size-36 bottom-2" />
                      </div>
                    }
                    title={t("emptyStateTitle")}
                    description={t("emptyStateDescription")}
                  />
                )}
            </motion.div>
          </>
        )}
      </main>

      {/* Stripe redirect handler with Suspense boundary */}
      <Suspense fallback={null}>
        <StripeRedirectHandler onNotification={handleNotification} />
      </Suspense>

      {/* List/Edit/Add modals and login modal moved into page scope */}

      {/* View shared list modal (for non-owners) */}
      {editingList?.isShared && (
        <ViewSharedListModal
          isOpen={!!editingList}
          listName={editListName}
          listColor={editListColor}
          colorOptions={LIST_COLORS}
          onClose={() => setEditingList(null)}
          onLeave={handleUnshareList}
        />
      )}

      {/* Edit list modal (for owners) */}
      {editingList && !editingList.isShared && (
        <ListModal
          isOpen={true}
          isEditMode={true}
          listId={editingList.id}
          listName={editListName}
          listColor={editListColor}
          colorOptions={LIST_COLORS}
          isShared={false}
          hasShares={editingList.hasShares}
          onClose={() => setEditingList(null)}
          onSave={handleSaveListEdit}
          onDelete={handleDeleteList}
          onNameChange={setEditListName}
          onColorChange={setEditListColor}
        />
      )}

      <ListModal
        isOpen={showAddListModal}
        isEditMode={false}
        listName={newListName}
        listColor={newListColor}
        colorOptions={LIST_COLORS}
        onClose={() => setShowAddListModal(false)}
        onSave={handleAddList}
        onNameChange={setNewListName}
        onColorChange={setNewListColor}
      />

      {/* Archive Modal */}
      <ArchiveModal
        isOpen={showArchive}
        onClose={() => setShowArchive(false)}
        archivedIngredients={archivedIngredients}
        onRestoreIngredient={toggleIngredient}
        onDeleteIngredient={deleteIngredient}
        onUpdateIngredient={updateIngredient}
        onIncrementQuantity={incrementQuantity}
        onDecrementQuantity={decrementQuantity}
      />

      {/* Voice Tip Modal */}
      <VoiceTipModal
        isOpen={showVoiceTip}
        onClose={() => {
          setShowVoiceTip(false);
          markVoiceTipAsSeen();
          // After closing the modal, start listening (which will request permission)
          startListening();
        }}
      />
    </>
  );
}
